import {
  forwardRef,
  useEffect,
  useId,
  useRef,
  type ButtonHTMLAttributes,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from 'react';
import { t } from '../../i18n';
import { cx } from '../../lib/cx';
import { errorText } from '../../lib/errors';
import { IconClose, IconSearch } from './Icon';

// ---------------------------------------------------------------- Button
type BtnVariant = 'outline' | 'solid' | 'ghost' | 'danger';
export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: BtnVariant;
  size?: 'md' | 'sm';
  busy?: boolean;
}
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'outline', size = 'md', busy, className, children, disabled, type = 'button', ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      className={cx(
        'btn',
        variant === 'solid' && 'btn-solid',
        variant === 'ghost' && 'btn-ghost',
        variant === 'danger' && 'btn-danger',
        size === 'sm' && 'btn-sm',
        className,
      )}
      disabled={disabled || busy}
      aria-busy={busy || undefined}
      {...rest}
    >
      {children}
    </button>
  );
});

/** Іконкова кнопка: обовʼязковий aria-label. */
export function IconButton({
  label,
  className,
  children,
  type = 'button',
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & { label: string }) {
  return (
    <button
      type={type}
      aria-label={label}
      title={label}
      className={cx(
        'inline-flex items-center justify-center w-8 h-8 rounded-lg text-lab hover:text-fg hover:bg-hov transition-colors disabled:opacity-40',
        className,
      )}
      {...rest}
    >
      {children}
    </button>
  );
}

/** Текстова кнопка-посилання. */
export function TextButton({ className, type = 'button', ...rest }: ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button
      type={type}
      className={cx('text-sm font-semibold text-accent hover:text-accent-hov underline-offset-4 hover:underline disabled:opacity-40 min-h-6', className)}
      {...rest}
    />
  );
}

// ---------------------------------------------------------------- Form fields
export function Field({
  label,
  hint,
  error,
  children,
  className,
  htmlFor,
}: {
  label: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  children: ReactNode;
  className?: string;
  htmlFor?: string;
}) {
  return (
    <div className={cx('flex flex-col gap-2 min-w-0', className)}>
      <label className="label" htmlFor={htmlFor}>
        {label}
      </label>
      {children}
      {hint && !error && <span className="text-xs text-lab">{hint}</span>}
      {error && (
        <span className="text-xs text-danger" role="alert">
          {error}
        </span>
      )}
    </div>
  );
}

type InputProps = InputHTMLAttributes<HTMLInputElement> & { label?: ReactNode; hint?: ReactNode; error?: ReactNode; wrapClass?: string };
export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { label, hint, error, wrapClass, className, id, ...rest },
  ref,
) {
  const auto = useId();
  const inputId = id ?? auto;
  const el = <input ref={ref} id={inputId} className={cx('input', className)} aria-invalid={error ? true : undefined} {...rest} />;
  if (!label) return el;
  return (
    <Field label={label} hint={hint} error={error} htmlFor={inputId} className={wrapClass}>
      {el}
    </Field>
  );
});

type SelectProps = SelectHTMLAttributes<HTMLSelectElement> & {
  label?: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  wrapClass?: string;
  options: { value: string; label: string; disabled?: boolean }[];
  placeholder?: string;
};
export function Select({ label, hint, error, wrapClass, className, id, options, placeholder, ...rest }: SelectProps) {
  const auto = useId();
  const selId = id ?? auto;
  const el = (
    <select id={selId} className={cx('input', className)} aria-invalid={error ? true : undefined} {...rest}>
      {placeholder !== undefined && <option value="">{placeholder}</option>}
      {options.map((o) => (
        <option key={o.value} value={o.value} disabled={o.disabled}>
          {o.label}
        </option>
      ))}
    </select>
  );
  if (!label) return el;
  return (
    <Field label={label} hint={hint} error={error} htmlFor={selId} className={wrapClass}>
      {el}
    </Field>
  );
}

type TextareaProps = TextareaHTMLAttributes<HTMLTextAreaElement> & { label?: ReactNode; hint?: ReactNode; wrapClass?: string };
export function Textarea({ label, hint, wrapClass, className, id, ...rest }: TextareaProps) {
  const auto = useId();
  const taId = id ?? auto;
  const el = <textarea id={taId} className={cx('textarea', className)} {...rest} />;
  if (!label) return el;
  return (
    <Field label={label} hint={hint} htmlFor={taId} className={wrapClass}>
      {el}
    </Field>
  );
}

export function Checkbox({
  label,
  className,
  ...rest
}: InputHTMLAttributes<HTMLInputElement> & { label: ReactNode }) {
  return (
    <label className={cx('inline-flex items-center gap-2.5 min-h-9 text-base cursor-pointer', className)}>
      <input type="checkbox" className="cb" {...rest} />
      <span>{label}</span>
    </label>
  );
}

/** Перемикач (role=switch), як у макеті Admin-Edit. */
export function Toggle({
  checked,
  onChange,
  label,
  disabled,
  className,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  label: ReactNode;
  disabled?: boolean;
  className?: string;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={cx('flex justify-between items-center gap-4 w-full min-h-9 text-base text-lab hover:text-fg aria-checked:text-fg disabled:opacity-50', className)}
    >
      <span>{label}</span>
      <i
        aria-hidden="true"
        className={cx('block relative w-9 h-5 rounded-full transition-colors flex-none', checked ? 'bg-accent' : 'bg-line-strong')}
      >
        <span
          className={cx(
            'absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-bg shadow transition-transform',
            checked && 'translate-x-4',
          )}
        />
      </i>
    </button>
  );
}

/** Радіо-група у вигляді кнопок з aria-pressed (як «Публікація» в макеті). */
export function RadioList<V extends string>({
  value,
  onChange,
  options,
  label,
}: {
  value: V;
  onChange: (v: V) => void;
  options: { value: V; label: string }[];
  label: string;
}) {
  return (
    <div role="radiogroup" aria-label={label} className="flex flex-col gap-1">
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          role="radio"
          aria-checked={value === o.value}
          onClick={() => onChange(o.value)}
          className="flex items-center gap-3 min-h-9 text-base text-lab hover:text-fg aria-checked:text-fg"
        >
          <i
            aria-hidden="true"
            className="relative w-4 h-4 rounded-full flex-none"
            style={{ boxShadow: `inset 0 0 0 ${value === o.value ? 2 : 1.5}px ${value === o.value ? 'var(--accent)' : 'var(--dim)'}` }}
          >
            {value === o.value && <span className="absolute inset-1 rounded-full bg-accent" />}
          </i>
          <span>{o.label}</span>
        </button>
      ))}
    </div>
  );
}

export function SearchInput({
  value,
  onChange,
  placeholder,
  label,
  className,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  label: string;
  className?: string;
}) {
  return (
    <label
      className={cx(
        'flex items-center gap-2 text-lab rounded-lg border border-line-strong bg-bg px-3 focus-within:border-accent focus-within:shadow-[0_0_0_3px_var(--accent-soft)] w-full sm:w-64 h-9 transition-shadow',
        className,
      )}
    >
      <IconSearch size={14} />
      <input
        type="search"
        className="flex-1 min-w-0 border-0 outline-none bg-transparent text-base text-fg placeholder:text-lab"
        value={value}
        placeholder={placeholder}
        aria-label={label}
        onChange={(e) => onChange(e.target.value)}
      />
    </label>
  );
}

// ---------------------------------------------------------------- Tabs
/** Вкладки сторінки (підкреслення): розділяють великий екран на частини. Для фільтрів — `Tabs`. */
export function PageTabs<V extends string>({
  value,
  onChange,
  items,
  label,
  className,
}: {
  value: V;
  onChange: (v: V) => void;
  items: { value: V; label: string; hint?: string | null }[];
  label: string;
  className?: string;
}) {
  return (
    <div role="tablist" aria-label={label} className={cx('flex gap-1 overflow-x-auto border-b border-line', className)}>
      {items.map((it) => {
        const on = value === it.value;
        return (
          <button
            key={it.value}
            type="button"
            role="tab"
            aria-selected={on}
            onClick={() => onChange(it.value)}
            className={cx(
              'flex-none inline-flex items-baseline gap-1.5 px-3 h-11 -mb-px border-b-2 text-base whitespace-nowrap',
              on ? 'border-fg text-fg font-semibold' : 'border-transparent text-lab hover:text-fg',
            )}
          >
            {it.label}
            {it.hint && <span className="text-xs text-lab font-normal tabular-nums">{it.hint}</span>}
          </button>
        );
      })}
    </div>
  );
}

export function Tabs<V extends string>({
  value,
  onChange,
  items,
  label,
  className,
}: {
  value: V;
  onChange: (v: V) => void;
  items: { value: V; label: string; count?: number | null }[];
  label: string;
  className?: string;
}) {
  return (
    <div role="tablist" aria-label={label} className={cx('tabs', className)}>
      {items.map((it) => (
        <button
          key={it.value}
          type="button"
          role="tab"
          aria-selected={value === it.value}
          className="tab"
          onClick={() => onChange(it.value)}
        >
          {it.label}
          {it.count != null && <sup>{it.count}</sup>}
        </button>
      ))}
    </div>
  );
}

/** Сегментний перемикач (кнопки з aria-pressed), як у макетах: країна, вид, період. */
export function Segmented<V extends string>({
  value,
  onChange,
  items,
  label,
  className,
  disabled,
}: {
  value: V;
  onChange: (v: V) => void;
  items: { value: V; label: string; count?: number | null }[];
  label: string;
  className?: string;
  disabled?: boolean;
}) {
  return (
    <div role="group" aria-label={label} className={cx('tabs', className)}>
      {items.map((it) => (
        <button
          key={it.value}
          type="button"
          aria-pressed={value === it.value}
          className="tab"
          disabled={disabled}
          onClick={() => onChange(it.value)}
        >
          {it.label}
          {it.count != null && <sup>{it.count}</sup>}
        </button>
      ))}
    </div>
  );
}

/** Смуга фільтрів під заголовком (як ad-bar). */
export function FilterBar({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div className={cx('flex justify-between items-center gap-x-4 gap-y-3 flex-wrap mb-4', className)}>
      {children}
    </div>
  );
}

// ---------------------------------------------------------------- Status
export type DotTone = 'fill' | 'ring' | 'dim' | 'danger' | 'ok' | 'warn' | 'accent' | 'neutral';
/** Статус: з підписом — кольорова «пігулка», без підпису — крапка. */
export function StatusDot({ tone, label, className }: { tone: DotTone; label?: ReactNode; className?: string }) {
  if (label == null) return <i className={cx('dot', `dot-${tone}`, className)} aria-hidden="true" />;
  return (
    <span className={cx('pill', `pill-${tone}`, className)}>
      <i className={cx('dot', `dot-${tone}`)} aria-hidden="true" />
      <span>{label}</span>
    </span>
  );
}

export function Progress({ value, max, label, className }: { value: number; max: number; label: string; className?: string }) {
  const pct = max > 0 ? Math.min(100, Math.max(0, (value / max) * 100)) : 0;
  return (
    <div
      role="progressbar"
      aria-label={label}
      aria-valuemin={0}
      aria-valuemax={max}
      aria-valuenow={value}
      className={cx('h-1.5 rounded-full bg-tile shadow-[inset_0_0_0_1px_var(--line)] relative overflow-hidden', className)}
    >
      <i className="absolute left-0 top-0 h-full bg-accent rounded-full block transition-[width] duration-500" style={{ width: `${pct}%` }} />
    </div>
  );
}

// ---------------------------------------------------------------- Layout bits
export function PageHeader({
  title,
  subtitle,
  actions,
  crumb,
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  actions?: ReactNode;
  crumb?: ReactNode;
}) {
  return (
    <div className="flex justify-between items-end gap-x-6 gap-y-4 flex-wrap mb-6">
      <div className="min-w-0">
        {crumb && <div className="text-sm text-mute mb-1.5">{crumb}</div>}
        <h1 className="display m-0 text-[32px] sm:text-[40px] leading-[1.1] break-words">{title}</h1>
        {subtitle && <p className="mt-1.5 mb-0 text-md text-lab">{subtitle}</p>}
      </div>
      {actions && <div className="flex gap-2.5 flex-wrap items-center">{actions}</div>}
    </div>
  );
}

export function Section({
  title,
  aside,
  children,
  className,
  id,
}: {
  title: ReactNode;
  aside?: ReactNode;
  children: ReactNode;
  className?: string;
  id?: string;
}) {
  const auto = useId();
  const hid = id ?? auto;
  return (
    <section className={cx('panel mb-4', className)} aria-labelledby={hid}>
      <div className="panel-head">
        <h2 className="sec" id={hid}>
          {title}
        </h2>
        {aside && <div className="text-xs font-medium text-lab text-right">{aside}</div>}
      </div>
      <div className="panel-body">{children}</div>
    </section>
  );
}

export function Kpi({ label, value, hint }: { label: ReactNode; value: ReactNode; hint?: ReactNode }) {
  return (
    <div className="kpi">
      <span>{label}</span>
      <b>{value}</b>
      {hint && <small className="block mt-2 text-xs text-lab">{hint}</small>}
    </div>
  );
}

export function KpiGrid({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cx('grid gap-4 mb-4 grid-cols-[repeat(auto-fit,minmax(min(190px,100%),1fr))]', className)}>{children}</div>;
}

// ---------------------------------------------------------------- States
export function Loading({ label = t('common.loading'), className }: { label?: string; className?: string }) {
  return (
    <div role="status" aria-live="polite" className={cx('py-8 text-base text-lab flex items-center gap-3', className)}>
      <span className="inline-block w-3.5 h-3.5 border-2 border-accent border-t-transparent rounded-full animate-spin" aria-hidden="true" />
      {label}
    </div>
  );
}

export function Empty({ children = t('common.empty'), className }: { children?: ReactNode; className?: string }) {
  return <div className={cx('py-8 text-base text-lab text-center', className)}>{children}</div>;
}

export function ErrorState({ error, onRetry, className }: { error: unknown; onRetry?: () => void; className?: string }) {
  return (
    <div role="alert" className={cx('py-8 text-base flex flex-col gap-3 items-start', className)}>
      <span className="text-danger">{t('common.error')}</span>
      <span className="text-sm text-lab">{errorText(error)}</span>
      {onRetry && (
        <Button size="sm" variant="ghost" onClick={onRetry}>
          {t('common.retry')}
        </Button>
      )}
    </div>
  );
}

export function InlineError({ error }: { error: unknown }) {
  if (!error) return null;
  return (
    <p role="alert" className="text-sm text-danger my-2">
      {errorText(error)}
    </p>
  );
}

// ---------------------------------------------------------------- Modal
export function Modal({
  open,
  onClose,
  title,
  children,
  footer,
  wide,
}: {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  wide?: boolean;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  // чи натиснули кнопку миші саме на тлі: виділення тексту в полі, відпущене над тлом, діалог не закриває
  const downOnBackdrop = useRef(false);
  const titleId = useId();
  useEffect(() => {
    const d = ref.current;
    if (!d) return;
    if (open && !d.open) {
      if (typeof d.showModal === 'function') d.showModal();
      else d.setAttribute('open', '');
    } else if (!open && d.open) {
      if (typeof d.close === 'function') d.close();
      else d.removeAttribute('open');
    }
  }, [open]);
  if (!open) return null;
  return (
    <dialog
      ref={ref}
      aria-labelledby={titleId}
      onCancel={(e) => {
        e.preventDefault();
        onClose();
      }}
      onMouseDown={(e) => {
        downOnBackdrop.current = e.target === ref.current;
      }}
      onClick={(e) => {
        // клік, що почався всередині (поле, кнопка) і закінчився на тлі, браузер шле на сам <dialog>
        const close = downOnBackdrop.current && e.target === ref.current;
        downOnBackdrop.current = false;
        if (close) onClose();
      }}
      className={cx(
        'bg-bg text-fg border border-line rounded-xl shadow-2xl p-0 m-auto w-[calc(100%-32px)] max-h-[calc(100vh-48px)] backdrop:bg-black/50',
        wide ? 'max-w-3xl' : 'max-w-lg',
      )}
    >
      <div className="flex flex-col max-h-[calc(100vh-50px)]">
        <div className="flex justify-between items-center gap-4 px-6 pt-5 pb-4 border-b border-line">
          <h2 id={titleId} className="m-0 text-lg font-bold">
            {title}
          </h2>
          <IconButton label={t('common.close')} onClick={onClose}>
            <IconClose />
          </IconButton>
        </div>
        <div className="px-6 py-5 overflow-y-auto">{children}</div>
        {footer && <div className="flex justify-end gap-3 flex-wrap px-6 py-4 border-t border-line bg-tile rounded-b-xl">{footer}</div>}
      </div>
    </dialog>
  );
}

export function FormGrid({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cx('grid gap-x-5 gap-y-5 grid-cols-[repeat(auto-fit,minmax(min(220px,100%),1fr))]', className)}>{children}</div>;
}

export function TableWrap({ children, minWidth = 760 }: { children: ReactNode; minWidth?: number }) {
  return (
    <div className="tbl-wrap overflow-x-auto">
      <div style={{ minWidth }}>{children}</div>
    </div>
  );
}

export function LoadMore({ hasMore, loading, onClick }: { hasMore: boolean; loading: boolean; onClick: () => void }) {
  if (!hasMore) return null;
  return (
    <div className="mt-4 flex justify-center">
      <Button size="sm" busy={loading} onClick={onClick}>
        {loading ? t('common.loading') : t('common.loadMore')}
      </Button>
    </div>
  );
}

/** Кружечок з ініціалами (відповідальний, автор). Без імені — порожній пунктир. */
export function Avatar({ name, className }: { name?: string | null; className?: string }) {
  const initials = (name ?? '')
    .split(/[\s@.]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]!.toUpperCase())
    .join('');
  return (
    <span
      aria-hidden="true"
      className={cx(
        'inline-flex items-center justify-center w-6 h-6 rounded-full flex-none text-2xs font-bold',
        initials ? 'bg-accent-soft text-fg' : 'border border-dashed border-line-strong',
        className,
      )}
    >
      {initials}
    </span>
  );
}
